namespace $.$$ {

	type $shm_projects_catalog_route = Record< string, string | null >

	export class $shm_projects_catalog extends $.$shm_projects_catalog {
		private project_route_synced = new Map< string, string >()

		auto() {
			return [
				... super.auto(),
				this.project_route_sync(),
				... this.development_events().map( events => events.native() ),
			]
		}

		@ $mol_mem
		projects() {
			return this.$.$shm_projects_order( this.$.$shm_projects_data.summaries()
				.filter( project => !project.archived )
				.filter( project => this.$.$shm_projects_access_view( project.id ) )
			)
		}

		project_id( id: string ) {
			return id
		}

		@ $mol_mem_key
		project_identifier( id: string ) {
			return this.$.$shm_projects_data.summary( id )?.fqn?.trim() || id
		}

		project_selected() {
			const id = this.$.$mol_state_arg.value( 'project' ) ?? ''
			return this.projects().some( project => project.id === id ) ? id : ''
		}

		@ $mol_mem
		projects_filtered() {
			const filter = this.filter().trim().toLowerCase()
			if( !filter ) return this.projects()
			const matched = new Set( this.projects()
				.filter( project => project.title.toLowerCase().includes( filter ) )
				.map( project => project.id ) )
			for( const project of this.projects() ) {
				if( !matched.has( project.id ) ) continue
				for( let parent = this.project_parent( project.id ); parent; parent = this.project_parent( parent.id ) ) matched.add( parent.id )
			}
			return this.projects().filter( project => matched.has( project.id ) )
		}

		@ $mol_mem_key
		project_parent( id: string ) {
			const parent = this.$.$shm_projects_data.summary( id )?.parent_id
			return parent ? this.$.$shm_projects_data.summary( parent ) : null
		}

		@ $mol_mem
		project_roots() {
			const visible = new Set( this.projects_filtered().map( project => project.id ) )
			return this.projects_filtered()
				.filter( project => !project.parent_id || !visible.has( project.parent_id ) )
				.map( project => project.id )
		}

		@ $mol_mem_key
		project_children( id: string ) {
			return this.projects_filtered().filter( project => project.parent_id === id ).map( project => project.id )
		}

		@ $mol_mem_key
		override project_expanded( id: string, next?: boolean ) {
			if( this.filter() ) return true
			return this.Projects().expanded_state( id, next )
		}

		project_expanded_key( id: string ) {
			return `shm_projects:catalog:expanded:${ id }`
		}

		@ $mol_action
		override add( next?: Event ) {
			if( next === undefined ) return null
			this.new_project_opened( true )
			return next
		}

		new_project_create_enabled() {
			return Boolean( this.new_project_title().trim() )
		}

		@ $mol_action
		override new_project_cancel( next?: Event ) {
			if( next === undefined ) return null
			this.new_project_title( '' )
			this.new_project_opened( false )
			return next
		}

		@ $mol_action
		override new_project_create( next?: Event ) {
			if( next === undefined ) return null
			const title = this.new_project_title().trim()
			if( !title ) return next
			const created = $mol_wire_sync( $shm_projects_goal_api ).project_create( title )
			this.$.$shm_projects_data.records_sync( created.projects )
			this.$.$shm_projects_data.detail_sync( created.project )
			this.new_project_title( '' )
			this.new_project_opened( false )
			this.$.$mol_state_arg.value( 'project', created.project.id )
			this.$.$mol_state_arg.value( 'project_tab', 'tasks' )
			this.$.$mol_state_arg.value( 'project_version', null )
			return next
		}

		project_title( id: string ) {
			return this.$.$shm_projects_data.summary( id )?.title ?? id
		}

		project_version( id: string ) {
			const version = this.$.$shm_projects_data.summary( id )?.version ?? ''
			return version ? `v${ version }` : '—'
		}

		project_prerelease( id: string ) {
			const version = this.$.$shm_projects_data.summary( id )?.channels?.prerelease ?? ''
			return version ? `v${ version }` : '—'
		}

		project_release( id: string ) {
			const version = this.$.$shm_projects_data.summary( id )?.channels?.release ?? ''
			return version ? `v${ version }` : '—'
		}

		project_version_date( id: string ) {
			const date = this.$.$shm_projects_data.summary( id )?.version_date ?? ''
			return date && date !== '-' ? date : '—'
		}

		project_version_record( id: string ) {
			const project = this.$.$shm_projects_data.record( id )
			return project?.history.find( version => version.version === project.version ) ?? project?.history[0] ?? null
		}

		project_version_arg( id: string ) {
			const version = this.project_version_record( id )
			return {
				... this.project_arg( id ),
				project_tab: 'history',
				project_version: version?.id ?? null,
				project_version_output: 'result',
			}
		}

		project_table_rows() {
			return this.projects_filtered().map( project => this.Project_row( project.id ) )
		}

		projects_arg() {
			return {
				project: null,
				... Object.fromEntries( this.project_route_keys().map( key => [ key, null ] ) ),
				goal_run: null,
				session: null,
			}
		}

		projects_uri() {
			return new this.$.$mol_state_arg( this.List_head().Title().state_key() ).link( this.projects_arg() )
		}

		private date_key( date: Date ) {
			const year = date.getFullYear()
			const month = String( date.getMonth() + 1 ).padStart( 2, '0' )
			const day = String( date.getDate() ).padStart( 2, '0' )
			return `${ year }-${ month }-${ day }`
		}

		private version_date_key( value: string ) {
			const ru = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec( value.trim() )
			if( ru ) return `${ ru[3] }-${ ru[2] }-${ ru[1] }`
			const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec( value.trim() )
			return iso ? `${ iso[1] }-${ iso[2] }-${ iso[3] }` : ''
		}

		timeline_days() {
			const today = new Date( this.$.$mol_state_time.now( 60_000 ) )
			today.setHours( 0, 0, 0, 0 )
			return Array.from( { length: 14 }, ( _, index )=> {
				const date = new Date( today )
				date.setDate( date.getDate() - 13 + index )
				return this.date_key( date )
			} )
		}

		timeline_day_rows() {
			return this.timeline_days().map( day => this.Timeline_day( day ) )
		}

		timeline_day_title( day: string ) {
			const date = new Date( `${ day }T12:00:00` )
			const weekday = new Intl.DateTimeFormat( 'ru-RU', { weekday: 'short' } ).format( date ).replace( '.', '' )
			const label = new Intl.DateTimeFormat( 'ru-RU', { day: '2-digit', month: 'short' } ).format( date ).replace( '.', '' )
			return `${ weekday }, ${ label }`
		}

		timeline_events( day: string ) {
			return this.$.$shm_projects_data.records().filter( project => !project.archived ).flatMap( project => {
				const versions = project.history.filter( version =>
					version.stage === 'Пререлиз' && this.version_date_key( version.date ) === day
				)
				return versions.length ? [{ project, version: versions[0], count: versions.length }] : []
			} )
		}

		timeline_event_rows( day: string ) {
			const events = this.timeline_events( day )
			return events.length
				? events.map( event => this.Timeline_event( JSON.stringify([ day, event.project.id ]) ) )
				: [ this.Timeline_empty( day ) ]
		}

		timeline_event( key: string ) {
			const [ day, project_id ] = JSON.parse( key ) as [ string, string ]
			return this.timeline_events( day ).find( event => event.project.id === project_id ) ?? null
		}

		timeline_event_title( key: string ) {
			const event = this.timeline_event( key )
			if( !event ) return 'Пререлиз'
			const count = event.count > 1 ? ` · ${ event.count } пререлиза` : ''
			return `${ event.project.title } · v${ event.version.version }${ count }`
		}

		timeline_event_arg( key: string ) {
			const event = this.timeline_event( key )
			if( !event ) return {}
			return {
				... this.project_arg( event.project.id ),
				project_tab: 'history',
				project_version: event.version.id,
				project_version_output: 'result',
			}
		}

		project_current( id: string ) {
			return this.project_selected() === id
		}

		project_route_keys() {
			return [
				'project_tab',
				'project_output',
				'project_task',
				'project_session',
				'project_version',
				'project_version_output',
				'project_thinking',
				'docs_file',
				'goal_run',
				'goal_iteration',
			] as const
		}

		project_route_current() {
			return Object.fromEntries( this.project_route_keys().map( key => [
				key,
				this.$.$mol_state_arg.value( key ),
			] ) ) as $shm_projects_catalog_route
		}

		@ $mol_mem_key
		project_route( id: string, next?: $shm_projects_catalog_route ) {
			return this.$.$mol_state_local.value< $shm_projects_catalog_route >(
				`shm_projects:catalog:route:${ id }`,
				next,
			) ?? {}
		}

		@ $mol_mem
		project_route_sync() {
			const project = this.project_selected()
			if( !project ) return null
			const route = this.project_route_current()
			const snapshot = JSON.stringify( route )
			if( this.project_route_synced.get( project ) === snapshot ) return null
			this.project_route_synced.set( project, snapshot )
			this.project_route( project, route )
			return null
		}

		@ $mol_mem_key
		project_developments( id: string ) {
			return this.$.$shm_projects_data.developments().flatMap( development => {
				const affected = development.projects.find( project => project.project_id === id )
				return affected ? [{
					id: development.id,
					source_project_id: development.source_project_id,
					source_project_title: development.source_project_title,
					status: development.status,
					busy: development.busy,
					phase: development.phase,
					updated_at: development.updated_at,
					changed_modules: development.changed_modules,
					affected_modules: development.affected_modules,
					reason: affected.reason,
				}] : []
			})
		}

		project_sessions_server_url() {
			const site = this.$.$shm_projects_config.site_api_url()
			if( site ) return `${ site }codex-api`
			const location = this.$.$mol_dom_context.location
			if( location.host.endsWith( '.158-160-195-195.ip.hyoo.ru' ) ) return 'https://codex-dev.158-160-195-195.ip.hyoo.ru'
			if( location.host === 'mam.138-124-242-95.ip.hyoo.ru' ) return `${ location.origin }/codex-api`
			return 'http://127.0.0.1:4870'
		}

		project_sessions_stamp() {
			return Math.floor( this.$.$mol_state_time.now( 2_000 ) / 2_000 )
		}

		@ $mol_mem
		project_sessions() {
			const server = this.project_sessions_server_url()
			const token = this.$.$shm_projects_config.agent_token()
			this.$.$shm_codex_api.token_provider( server, ()=> token )
			return this.$.$mol_error_fence(
				()=> this.$.$shm_wire_latest( server, ()=> $mol_wire_sync( $shm_codex_api ).sessions(
					server,
					this.project_sessions_stamp(),
					'[project',
				).data ),
				()=> [] as $shm_codex_api_thread[],
				loading => $mol_fail_hidden( loading ),
			)
		}

		@ $mol_mem_key
		project_session_busy( id: string ) {
			const prefixes = [ `[project:${ id }] `, `[project-session:${ id }] ` ]
			return this.project_sessions().some( session =>
				session.status?.type === 'active'
					&& prefixes.some( prefix => session.name?.startsWith( prefix ) ),
			)
		}

		@ $mol_mem_key
		project_read_at( id: string, next?: number ) {
			return this.$.$mol_state_local.value< number >( `shm_projects:catalog:read:${ id }`, next ) ?? 0
		}

		@ $mol_mem_key
		project_activity_status( id: string ) {
			const developments = this.project_developments( id )
			if( developments.some( development => development.busy ) || this.project_session_busy( id ) ) return 'busy'
			if( developments.some( development => development.updated_at > this.project_read_at( id ) ) ) return 'unread'
			return developments.length ? 'affected' : ''
		}

		project_activity_title( id: string ) {
			const count = this.project_developments( id ).length
			switch( this.project_activity_status( id ) ) {
				case 'busy': return this.project_session_busy( id )
					? 'В проекте сейчас работает сессия'
					: 'В проекте сейчас работает агент'
				case 'unread': return 'В проекте появились непрочитанные изменения'
				case 'affected': return count === 1 ? 'Проект затронут разработкой' : `Проект затронут разработками: ${ count }`
				default: return ''
			}
		}

		project_activity_content( id: string ) {
			const status = this.project_activity_status( id )
			return status === 'unread' || status === 'affected' ? [ '●' ] : []
		}

		@ $mol_action
		override project_read( id: string, next?: Event ) {
			if( next === undefined ) return null
			const current = this.project_selected()
			if( current ) this.project_route( current, this.project_route_current() )
			const updated = Math.max( 0, ...this.project_developments( id ).map( development => development.updated_at ) )
			this.project_read_at( id, updated )
			return next
		}

		development_ids() {
			return this.$.$shm_projects_data.developments().map( development => development.id )
		}

		@ $mol_mem_key
		Development_event( id: string ) {
			const events = new this.$.$shm_projects_goal_api_events
			events.id = ()=> id
			events.notify = ()=> run => this.development_receive( run )
			return events
		}

		development_events() {
			return this.development_ids().map( id => this.Development_event( id ) )
		}

		@ $mol_action
		development_receive( run: $shm_projects_goal_run ) {
			const development = run.development
			const open = !run.deployments.some( deployment =>
				deployment.stage === 'Релиз'
					|| deployment.stage === 'Пререлиз'
						&& deployment.status === 'deployed'
						&& Boolean( deployment.finalized_at )
			)
			const developments = this.$.$shm_projects_data.developments()
				.filter( known => known.id !== run.id )
			if( open && development ) developments.push( development )
			this.$.$shm_projects_data.developments( developments )
			return run
		}

		project_arg( id: string ) {
			return {
				project: id,
				... Object.fromEntries( this.project_route_keys().map( key => [ key, null ] ) ),
				project_tab: 'tasks',
				goal_run: null,
				session: null,
				... ( id === this.project_selected() ? this.project_route_current() : this.project_route( id ) ),
			}
		}

		catalog_pages() {
			const project = this.project_selected()
			const details = this.new_project_opened()
				? [ this.New_project_page() ]
				: project ? this.Project( project ).pages_deep() : [ this.Catalog_page() ]
			return this.projects_list_visible()
				? [ this.Menu_page(), ...details ]
				: details
		}

	}

}
